<template>
  <div class="q-pa-md">
    <div class="row q-mb-md">
      <div class="col">
        <h4 class="q-ma-none">{{ t('facebook.conversions.title') }}</h4>
        <p class="text-grey q-ma-none">{{ t('facebook.conversions.history') }}</p>
      </div>
      <div class="col-auto">
        <q-btn
            color="secondary"
            icon="add"
            :label="t('facebook.conversions.add')"
            @click="showAddConversionDialog = true"
            class="q-mr-sm"
        />
        <q-btn
            color="primary"
            icon="refresh"
            :label="t('common.refresh')"
            @click="fetchConversions()"
            :loading="loading"
        />
      </div>
    </div>

    <!-- Фильтры -->
    <q-card flat class="q-mb-md">
      <q-card-section>
        <div class="row q-gutter-sm items-end">
          <div class="col-lg-2 col-md-3 col-sm-4 col-xs-6">
            <q-select
                v-model="filters.status"
                :options="statusOptions"
                emit-value
                map-options
                clearable
                :label="t('facebook.conversions.filters.by_status')"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div class="col-lg-2 col-md-3 col-sm-4 col-xs-6">
            <q-select
                v-model="filters.eventName"
                :options="eventNameOptions"
                emit-value
                map-options
                clearable
                :label="t('facebook.conversions.filters.by_event')"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div class="col-lg-2 col-md-3 col-sm-4 col-xs-6">
            <q-select
                v-model="filters.pixelId"
                :options="filteredPixelOptions"
                emit-value
                map-options
                clearable
                use-input
                input-debounce="200"
                :label="t('facebook.conversions.filters.by_pixel')"
                @filter="filterPixels"
                @filter-abort="abortFilterPixels"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div class="col-lg-2 col-md-3 col-sm-4 col-xs-6" v-if="isAdmin">
            <q-select
                v-model="filters.ownerId"
                :options="filteredOwnerOptions"
                emit-value
                map-options
                clearable
                :label="t('facebook.conversions.filters.by_owner')"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div class="col-lg-2 col-md-3 col-sm-4 col-xs-6">
            <q-select
                v-model="filters.datePeriod"
                :options="datePeriodOptions"
                emit-value
                map-options
                :label="t('facebook.conversions.filters.date_period')"
                @update:model-value="onDatePeriodChange"
                dense
            />
          </div>
          <div v-if="filters.datePeriod === 'custom'" class="col-lg-1 col-md-2 col-sm-3 col-xs-6">
            <q-input
                v-model="filters.dateFrom"
                type="date"
                :label="t('common.date_from')"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div v-if="filters.datePeriod === 'custom'" class="col-lg-1 col-md-2 col-sm-3 col-xs-6">
            <q-input
                v-model="filters.dateTo"
                type="date"
                :label="t('common.date_to')"
                @update:model-value="onFilterChange"
                dense
            />
          </div>
          <div class="col-auto">
            <q-btn
                flat
                round
                icon="clear_all"
                @click="clearFilters"
                :disable="!hasActiveFilters"
                size="sm"
            >
              <q-tooltip>{{ t('facebook.conversions.filters.clear') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>

    <!-- Статистика -->
    <div class="row q-gutter-md q-mb-md">
      <div class="col">
        <q-card flat bordered>
          <q-card-section class="text-center">
            <div class="text-h4 text-positive">{{ stats.successful }}</div>
            <div class="text-caption">{{ t('facebook.conversions.stats.successful') }}</div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col">
        <q-card flat bordered>
          <q-card-section class="text-center">
            <div class="text-h4 text-negative">{{ stats.failed }}</div>
            <div class="text-caption">{{ t('facebook.conversions.stats.failed') }}</div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col">
        <q-card flat bordered>
          <q-card-section class="text-center">
            <div class="text-h4 text-warning">{{ stats.pending }}</div>
            <div class="text-caption">{{ t('facebook.conversions.stats.processing') }}</div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Таблица конверсий с поддержкой URL -->
    <q-table
        :rows="conversions"
        :columns="columns"
        row-key="id"
        :loading="loading"
        :pagination="pagination"
        @request="onRequest"
        binary-state-sort
        :rows-per-page-options="[10, 20, 50, 100]"
        :filter="tableFilter"
        v-model:pagination="pagination"
    >
      <template v-slot:body-cell-status="props">
        <q-td :props="props">
          <q-chip
              :color="getStatusColor(props.value)"
              :icon="getStatusIcon(props.value)"
              text-color="white"
              dense
          >
            {{ getStatusText(props.value) }}
          </q-chip>
        </q-td>
      </template>

      <template v-slot:body-cell-status_event="props">
        <q-td :props="props">
          <q-chip
              :color="getStatusColor(props.row.status)"
              :icon="getStatusIcon(props.row.status)"
              text-color="white"
              dense
          >
            {{ props.row.event_name }}
          </q-chip>
        </q-td>
      </template>

      <!-- URL сайта (из БД) -->
      <template v-slot:body-cell-source_url="props">
        <q-td :props="props">
          <div v-if="props.value" class="url-cell">
            <q-btn
                :label="formatUrl(props.value)"
                :href="props.value"
                target="_blank"
                flat
                dense
                color="primary"
                icon="link"
                class="text-left url-link"
                style="max-width: 200px; text-transform: none;"
            />
            <q-tooltip>{{ props.value }}</q-tooltip>
          </div>
          <div v-else-if="getEventSourceUrl(props.row)" class="url-cell">
            <q-btn
                :label="formatUrl(getEventSourceUrl(props.row))"
                :href="getEventSourceUrl(props.row)"
                target="_blank"
                flat
                dense
                color="secondary"
                icon="facebook"
                class="text-left url-link"
                style="max-width: 200px; text-transform: none;"
            />
            <q-tooltip>Facebook API: {{ getEventSourceUrl(props.row) }}</q-tooltip>
          </div>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <!-- URL в Facebook API -->
      <template v-slot:body-cell-event_source_url="props">
        <q-td :props="props">
          <div v-if="getEventSourceUrl(props.row)" class="url-cell">
            <q-btn
                :label="formatUrl(getEventSourceUrl(props.row))"
                :href="getEventSourceUrl(props.row)"
                target="_blank"
                flat
                dense
                color="secondary"
                icon="facebook"
                class="text-left url-link"
                style="max-width: 200px; text-transform: none;"
            />
            <q-tooltip>
              Facebook API: {{ getEventSourceUrl(props.row) }}
            </q-tooltip>
          </div>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-quality_score="props">
        <q-td :props="props">
          <div v-if="props.value !== null" class="flex items-center justify-center">
            <q-circular-progress
                :value="props.value * 10"
                size="30px"
                :thickness="0.3"
                :color="getQualityColor(props.value)"
                track-color="grey-3"
                class="q-mr-xs"
            />
            <span :class="`text-${getQualityColor(props.value)}`">
              {{ props.value }}/10
            </span>
          </div>
          <span v-else class="text-grey">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-identifiers_count="props">
        <q-td :props="props">
          <q-badge
              v-if="props.value !== null"
              :color="props.value >= 2 ? 'positive' : props.value >= 1 ? 'warning' : 'negative'"
              :label="props.value + '/9'"
          />
          <span v-else class="text-grey">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-facebook_trace_id="props">
        <q-td :props="props">
          <div v-if="props.value" class="trace-id-cell">
            <q-btn
                :label="props.value.substring(0, 8) + '...'"
                flat
                dense
                color="info"
                @click="copyToClipboard(props.value)"
            />
            <q-tooltip>{{ props.value }} ({{ t('facebook.conversions.table.click_to_copy') }})</q-tooltip>
          </div>
          <span v-else class="text-grey">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-fbc="props">
        <q-td :props="props">
          <div v-if="getFBC(props.row)" class="trace-id-cell" @click="copyToClipboard(getFBC(props.row))">
            {{getFBC(props.row).length > 8 ? getFBC(props.row).substring(0, 8) + '...' : getFBC(props.row)}}

            <q-tooltip>{{ getFBC(props.row) }} ({{ t('facebook.conversions.table.click_to_copy') }})</q-tooltip>
          </div>
          <span v-else class="text-grey">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-external_id="props">
        <q-td :props="props">
          <div v-if="getExternalId(props.row)" class="trace-id-cell">
            <span @click="copyToClipboard(getExternalId(props.row))">{{getExternalId(props.row)}}</span>
            <q-tooltip>{{ getExternalId(props.row) }} ({{ t('facebook.conversions.table.click_to_copy') }})</q-tooltip>
          </div>
          <span v-else class="text-grey">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-actions="props">
        <q-td :props="props">
          <q-btn
              icon="edit"
              flat
              dense
              @click="editConversion(props.row)"
              color="warning"
              :disable="props.row.status === 'pending'"
              class="q-mr-xs"
          >
            <q-tooltip>{{ t('common.edit') }}</q-tooltip>
          </q-btn>
          <q-btn
              icon="visibility"
              flat
              dense
              @click="showDetails(props.row)"
              color="primary"
          >
            <q-tooltip>{{ t('facebook.conversions.table.show_details') }}</q-tooltip>
          </q-btn>
        </q-td>
      </template>
    </q-table>

    <!-- Диалог добавления конверсии -->
    <q-dialog v-model="showAddConversionDialog" persistent>
      <q-card style="min-width: 500px; max-width: 600px; width: 100%;">
        <QuickConversionForm
            ref="quickConversionFormRef"
            :autoFetchPixels="true"
            @conversion-sent="onConversionAdded"
        />
        <q-card-actions align="right">
          <q-btn
              flat
              :label="t('common.cancel')"
              @click="showAddConversionDialog = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <!-- Диалог редактирования конверсии -->
    <q-dialog v-model="showEditDialog" persistent>
      <q-card style="min-width: 500px; max-width: 600px; width: 100%;">
        <QuickConversionForm
            ref="editConversionFormRef"
            :autoFetchPixels="false"
            :editMode="true"
            :editConversion="currentEditConversion"
            @conversion-sent="onConversionEdited"
        />
        <q-card-actions align="right">
          <q-btn
              flat
              label="Отмена"
              @click="showEditDialog = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <!-- Диалог детальной информации -->
    <q-dialog v-model="showDetailsDialog">
      <q-card style="min-width: 800px; max-width: 1200px; width: 90vw; max-height: 90vh;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ t('facebook.conversions.details.title', { id: currentConversion?.id }) }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section style="max-height: 80vh; overflow-y: auto;">
          <div class="row q-gutter-md">
            <!-- Основная информация -->
            <div class="col-md-4 col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1 q-mb-md">📊 {{ t('facebook.conversions.details.basic_info') }}</div>
                  <q-list dense>
                    <q-item>
                      <q-item-section avatar>
                        <q-icon name="fingerprint" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.conversion_id') }}</q-item-label>
                        <q-item-label caption>{{ currentConversion?.id }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item>
                      <q-item-section avatar>
                        <q-icon name="ads_click" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.pixel') }}</q-item-label>
                        <q-item-label caption>{{ currentConversion?.pixel_id }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item>
                      <q-item-section avatar>
                        <q-icon name="account_box" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.account') }}</q-item-label>
                        <q-item-label caption>{{ currentConversion?.account_name }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item>
                      <q-item-section avatar>
                        <q-icon name="event" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.table.event') }}</q-item-label>
                        <q-item-label caption>{{ currentConversion?.event_name }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item>
                      <q-item-section avatar>
                        <q-icon name="tag" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Event ID</q-item-label>
                        <q-item-label caption>{{ currentConversion?.event_id }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item>
                      <q-item-section avatar>
                        <q-icon :name="getStatusIcon(currentConversion?.status)" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.status') }}</q-item-label>
                        <q-item-label>
                          <q-chip
                              :color="getStatusColor(currentConversion?.status)"
                              text-color="white"
                              size="sm"
                          >
                            {{ getStatusText(currentConversion?.status) }}
                          </q-chip>
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-card-section>
              </q-card>
            </div>

            <!-- URL информация -->
            <div class="col-md-4 col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1 q-mb-md">🌐 {{ t('facebook.conversions.details.url_info') }}</div>
                  <q-list dense>
                    <q-item v-if="currentConversion?.source_url">
                      <q-item-section avatar>
                        <q-icon name="link" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.site_url_db') }}</q-item-label>
                        <q-item-label caption>
                          <a :href="currentConversion.source_url" target="_blank" class="text-primary">
                            {{ currentConversion.source_url }}
                          </a>
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="getEventSourceUrl(currentConversion)">
                      <q-item-section avatar>
                        <q-icon name="facebook" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.url_in_facebook_api') }}</q-item-label>
                        <q-item-label caption>
                          <a :href="getEventSourceUrl(currentConversion)" target="_blank" class="text-secondary">
                            {{ getEventSourceUrl(currentConversion) }}
                          </a>
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="getReferrerUrl(currentConversion)">
                      <q-item-section avatar>
                        <q-icon name="call_made" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.referrer') }}</q-item-label>
                        <q-item-label caption>
                          <a :href="getReferrerUrl(currentConversion)" target="_blank" class="text-orange">
                            {{ getReferrerUrl(currentConversion) }}
                          </a>
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="!currentConversion?.source_url && !getEventSourceUrl(currentConversion)">
                      <q-item-section avatar>
                        <q-icon name="warning" color="orange" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.url_not_specified') }}</q-item-label>
                        <q-item-label caption class="text-orange">
                          {{ t('facebook.conversions.details.url_recommendation') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-card-section>
              </q-card>
              <div class="col-12" v-if="getValidationWarnings(currentConversion)">
                <q-card flat bordered>
                  <q-card-section>
                    <div class="text-subtitle1 q-mb-md">⚠️ {{ t('facebook.conversions.details.validation_warnings') }}</div>
                    <q-list dense bordered>
                      <q-item v-for="warning in getValidationWarnings(currentConversion)" :key="warning">
                        <q-item-section avatar>
                          <q-icon name="warning" color="orange" size="sm" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ warning }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
            </div>

            <!-- Качество данных -->
            <div class="col-md-4 col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1 q-mb-md">⭐ {{ t('facebook.conversions.details.data_quality') }}</div>
                  <q-list dense>
                    <q-item v-if="currentConversion?.quality_score !== null">
                      <q-item-section avatar>
                        <q-circular-progress
                            :value="currentConversion.quality_score * 10"
                            size="40px"
                            :thickness="0.2"
                            :color="getQualityColor(currentConversion.quality_score)"
                            track-color="grey-3"
                        />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.quality_score') }}</q-item-label>
                        <q-item-label caption>
                          {{ currentConversion.quality_score }}/10 ({{ getQualityText(currentConversion.quality_score) }})
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="currentConversion?.identifiers_count">
                      <q-item-section avatar>
                        <q-icon name="how_to_reg" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ t('facebook.conversions.details.identifiers') }}</q-item-label>
                        <q-item-label caption>{{ t('facebook.conversions.details.identifiers_count', {
                          current: currentConversion.identifiers_count,
                          total: 9
                        }) }}</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="currentConversion?.facebook_trace_id">
                      <q-item-section avatar>
                        <q-icon name="bug_report" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Facebook Trace ID</q-item-label>
                        <q-item-label caption>
                          <q-btn
                              :label="currentConversion.facebook_trace_id"
                              flat
                              dense
                              color="info"
                              @click="copyToClipboard(currentConversion.facebook_trace_id)"
                          />
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-card-section>
              </q-card>
            </div>

            <!-- Custom Data -->
            <div class="col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1 q-mb-md">📦 {{ t('facebook.conversions.details.custom_data') }}</div>
                  <pre class="custom-data-json">{{ formatCustomData(currentConversion?.custom_data) }}</pre>
                </q-card-section>
              </q-card>
            </div>

            <!-- User Data -->
            <div class="col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1 q-mb-md">👤 {{ t('facebook.conversions.details.user_data_hashed') }}</div>
                  <pre class="custom-data-json">{{ formatCustomData(currentConversion?.user_data) }}</pre>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import api from '@/api/api'
import QuickConversionForm from '@/components/QuickConversionForm.vue'

const $q = useQuasar()
const { t } = useI18n()

// Reactive data
const conversions = ref([])
const loading = ref(false)
const showDetailsDialog = ref(false)
const showAddConversionDialog = ref(false)
const currentConversion = ref(null)
const allPixelOptions = ref([])
const filteredPixelOptions = ref([])
const filteredOwnerOptions = ref([])
const isAdmin = computed(() => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  return user.role === 'admin'
})

const getStorageValue = (key, defaultValue = '') => {
  const value = localStorage.getItem(key)
  return (value === null || value === 'null') ? defaultValue : value
}

const filters = ref({
  status: getStorageValue('conversions_filter_status'),
  eventName: getStorageValue('conversions_filter_eventName'),
  pixelId: getStorageValue('conversions_filter_pixelId'),
  ownerId: getStorageValue('conversions_filter_ownerId'),
  datePeriod: getStorageValue('conversions_filter_datePeriod', 'today'),
  dateFrom: getStorageValue('conversions_filter_dateFrom', new Date().toISOString().substr(0, 10)),
  dateTo: getStorageValue('conversions_filter_dateTo', new Date().toISOString().substr(0, 10))
})

const datePeriodOptions = computed(() => [
  { label: t('facebook.conversions.filters.today'), value: 'today' },
  { label: t('facebook.conversions.filters.yesterday'), value: 'yesterday' },
  { label: t('facebook.conversions.filters.this_week'), value: 'this_week' },
  { label: t('facebook.conversions.filters.last_week'), value: 'last_week' },
  { label: t('facebook.conversions.filters.this_month'), value: 'this_month' },
  { label: t('facebook.conversions.filters.last_month'), value: 'last_month' },
  { label: t('facebook.conversions.filters.custom'), value: 'custom' }
])

const pagination = ref({
  page: 1,
  rowsPerPage: 20,
  rowsNumber: 0,
  sortBy: 'created_at',
  descending: true
})

// ref для формы быстрой конверсии
const quickConversionFormRef = ref(null)

// Диалог редактирования
const showEditDialog = ref(false)
const currentEditConversion = ref(null)
const editConversionFormRef = ref(null)

// Метод открытия редактирования
const editConversion = (conversion) => {
  currentEditConversion.value = conversion
  showEditDialog.value = true
}

// Обработка успешного редактирования
const onConversionEdited = () => {
  $q.notify({
    type: 'positive',
    message: 'Конверсия успешно обновлена',
    position: 'top'
  })
  showEditDialog.value = false
  fetchConversions() // Обновляем список
}

// Options для фильтров
const statusOptions = computed(() => [
  { label: t('facebook.conversions.options.all_statuses'), value: '' },
  { label: t('facebook.conversions.status.successful'), value: 'success' },
  { label: t('facebook.conversions.status.failed'), value: 'failed' },
  { label: t('facebook.conversions.status.processing'), value: 'pending' }
])

const eventNameOptions = ref([])
const pixelOptions = ref([])

// Фильтр для таблицы
const tableFilter = ref('')

// Колонки для таблицы
const columns = computed(() => {
  const cols = []
  // ID только для админа
  if (isAdmin.value) {
    cols.push({ name: 'id', label: 'ID', field: 'id', align: 'left', sortable: true })
  }
  cols.push(
      { name: 'status_event', label: t('facebook.conversions.table.status'), field: 'status', align: 'center' },
      { name: 'pixel_id', label: t('facebook.conversions.table.pixel'), field: row => row.pixel_name || row.pixel_id, align: 'center' },
      { name: 'account_name', label: t('facebook.conversions.table.account'), field: 'account_name', align: 'center' }
  )
  // Владелец только для админа
  if (isAdmin.value) {
    cols.push({ name: 'owner', label: t('facebook.conversions.table.owner'), field: 'owner_username', align: 'center' })
  }
  cols.push(
      { name: 'source_url', label: t('facebook.conversions.table.site_url'), field: 'source_url', align: 'center' },
      { name: 'fbc', label: 'FBClid', field: 'fbc', align: 'center' },
      { name: 'external_id', label: 'External ID', field: 'external_id', align: 'center' },
      { name: 'created_at', label: t('facebook.conversions.table.created'), field: 'created_at', align: 'center', sortable: true },
      { name: 'actions', label: t('facebook.conversions.table.actions'), field: 'actions', align: 'center' }
  )
  return cols
})

const stats = ref({
  successful: 0,
  failed: 0,
  pending: 0,
  total: 0,
  avgQuality: 0
})

const hasActiveFilters = computed(() => {
  return !!(
      (filters.value.status && filters.value.status !== '') ||
      (filters.value.eventName && filters.value.eventName !== '') ||
      (filters.value.pixelId && filters.value.pixelId !== '') ||
      (filters.value.ownerId && filters.value.ownerId !== '') ||
      filters.value.datePeriod !== 'today'
  )
})

const filterPixels = (val, update, abort) => {
  setTimeout(() => {
    update(
        () => {
          if (val === '') {
            filteredPixelOptions.value = allPixelOptions.value
          } else {
            const needle = val.toLowerCase()
            filteredPixelOptions.value = allPixelOptions.value.filter(
                v => v.label.toLowerCase().indexOf(needle) > -1
            )
          }
        },
        ref => {
          if (val !== '' && ref.options.length > 0 && ref.getOptionIndex() === -1) {
            ref.setOptionIndex(-1)
            ref.moveOptionSelection(1, true)
            if (ref.options.length === 1) {
              ref.toggleOption(ref.options[ref.getOptionIndex()], true)
            }
          }
        }
    )
  }, 100)
}

const abortFilterPixels = () => {
  // Сброс фильтра при отмене
}

// Methods
const fetchConversions = async (props = {}) => {
  loading.value = true

  try {
    const params = new URLSearchParams()

    // Пагинация
    const currentPagination = props.pagination || pagination.value
    params.append('limit', currentPagination.rowsPerPage)
    params.append('offset', (currentPagination.page - 1) * currentPagination.rowsPerPage)

    // Сортировка
    if (currentPagination.sortBy) {
      params.append('sort_by', currentPagination.sortBy)
      params.append('sort_order', currentPagination.descending ? 'desc' : 'asc')
    }

    // Фильтры
    if (filters.value.status && filters.value.status !== '') params.append('status', filters.value.status)
    if (filters.value.eventName && filters.value.eventName !== '') params.append('event_name', filters.value.eventName)
    if (filters.value.pixelId && filters.value.pixelId !== '') params.append('pixel_id', filters.value.pixelId)
    if (filters.value.ownerId && filters.value.ownerId !== '') params.append('owner_id', filters.value.ownerId)
    if (filters.value.dateFrom) params.append('date_from', filters.value.dateFrom)
    if (filters.value.dateTo) params.append('date_to', filters.value.dateTo)

    const { data } = await api.get(`facebook-conversions?${params.toString()}`)
    conversions.value = data.conversions
    stats.value = data.stats || stats.value

    // Обновляем опции фильтров с данными сервера
    updateFilterOptions(data.filter_options)

    // Обновляем пагинацию
    if (props.pagination) {
      pagination.value = { ...currentPagination, rowsNumber: data.total }
    } else {
      pagination.value.rowsNumber = data.total
      pagination.value.page = currentPagination.page
      pagination.value.rowsPerPage = currentPagination.rowsPerPage
    }
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: t('notifications.accounts_load_error', { error: error.message })
    })
  } finally {
    loading.value = false
  }
}

const updateFilterOptions = (serverOptions = null) => {
  if (serverOptions) {
    // Используем опции с сервера (все доступные)
    eventNameOptions.value = [
      { label: t('facebook.conversions.options.all_events'), value: '' },
      ...serverOptions.events.map(event => ({ label: event, value: event }))
    ]

    allPixelOptions.value = [
      { label: t('facebook.conversions.options.all_pixels'), value: '' },
      ...serverOptions.pixels
    ]

    // Добавить обработку владельцев
    if (serverOptions.owners && isAdmin.value) {
      filteredOwnerOptions.value = [
        { label: t('facebook.conversions.options.all_owners'), value: '' },
        ...serverOptions.owners
      ]
    }
  } else {
    // Fallback: используем данные с текущей страницы
    const uniqueEvents = [...new Set(conversions.value.map(c => c.event_name))]
    eventNameOptions.value = [
      { label: t('facebook.conversions.options.all_events'), value: '' },
      ...uniqueEvents.map(event => ({ label: event, value: event }))
    ]

    const pixelMap = new Map()
    conversions.value.forEach(c => {
      if (c.pixel_id) {
        pixelMap.set(c.pixel_id, {
          label: c.pixel_name || c.pixel_id,
          value: c.pixel_id,
          sublabel: c.pixel_name ? `ID: ${c.pixel_id}` : null
        })
      }
    })

    allPixelOptions.value = [
      { label: t('facebook.conversions.options.all_pixels'), value: '' },
      ...Array.from(pixelMap.values())
    ]

    // Добавить обработку владельцев из текущих данных
    if (isAdmin.value) {
      const ownerMap = new Map()
      conversions.value.forEach(c => {
        if (c.owner_username) {
          ownerMap.set(c.owner_username, {
            label: c.owner_username,
            value: c.owner_username
          })
        }
      })

      filteredOwnerOptions.value = [
        { label: t('facebook.conversions.options.all_owners'), value: '' },
        ...Array.from(ownerMap.values())
      ]
    }
  }

  filteredPixelOptions.value = [...allPixelOptions.value]
}

const onRequest = (props) => {
  fetchConversions(props)
}

const onFilterChange = () => {
  // Преобразуем null в пустые строки
  if (filters.value.status === null) filters.value.status = ''
  if (filters.value.eventName === null) filters.value.eventName = ''
  if (filters.value.pixelId === null) filters.value.pixelId = ''
  if (filters.value.ownerId === null) filters.value.ownerId = ''

  // Сохраняем фильтры в localStorage
  localStorage.setItem('conversions_filter_status', filters.value.status)
  localStorage.setItem('conversions_filter_eventName', filters.value.eventName)
  localStorage.setItem('conversions_filter_pixelId', filters.value.pixelId)
  localStorage.setItem('conversions_filter_ownerId', filters.value.ownerId)
  localStorage.setItem('conversions_filter_datePeriod', filters.value.datePeriod)
  localStorage.setItem('conversions_filter_dateFrom', filters.value.dateFrom)
  localStorage.setItem('conversions_filter_dateTo', filters.value.dateTo)

  pagination.value.page = 1
  fetchConversions()
}

// метод обработки периода дат
const onDatePeriodChange = () => {
  const today = new Date()
  const period = filters.value.datePeriod

  switch(period) {
    case 'today':
      filters.value.dateFrom = today.toISOString().substr(0, 10)
      filters.value.dateTo = today.toISOString().substr(0, 10)
      break
    case 'yesterday':
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      filters.value.dateFrom = yesterday.toISOString().substr(0, 10)
      filters.value.dateTo = yesterday.toISOString().substr(0, 10)
      break
    case 'this_week':
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay() + 1)
      filters.value.dateFrom = startOfWeek.toISOString().substr(0, 10)
      filters.value.dateTo = today.toISOString().substr(0, 10)
      break
    case 'last_week':
      const lastWeekEnd = new Date(today)
      lastWeekEnd.setDate(today.getDate() - today.getDay())
      const lastWeekStart = new Date(lastWeekEnd)
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6)
      filters.value.dateFrom = lastWeekStart.toISOString().substr(0, 10)
      filters.value.dateTo = lastWeekEnd.toISOString().substr(0, 10)
      break
    case 'this_month':
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      filters.value.dateFrom = startOfMonth.toISOString().substr(0, 10)
      filters.value.dateTo = today.toISOString().substr(0, 10)
      break
    case 'last_month':
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
      filters.value.dateFrom = lastMonthStart.toISOString().substr(0, 10)
      filters.value.dateTo = lastMonthEnd.toISOString().substr(0, 10)
      break
    case 'custom':
      // Для кастомного ввода даты остаются как есть
      return
  }

  // Сохраняем в localStorage
  localStorage.setItem('conversions_filter_datePeriod', filters.value.datePeriod)
  localStorage.setItem('conversions_filter_dateFrom', filters.value.dateFrom)
  localStorage.setItem('conversions_filter_dateTo', filters.value.dateTo)

  onFilterChange()
}

const clearFilters = () => {
  filters.value = {
    status: '',
    eventName: '',
    pixelId: '',
    ownerId: '',
    datePeriod: 'today',
    dateFrom: new Date().toISOString().substr(0, 10),
    dateTo: new Date().toISOString().substr(0, 10)
  }

  // Очищаем localStorage
  localStorage.removeItem('conversions_filter_status')
  localStorage.removeItem('conversions_filter_eventName')
  localStorage.removeItem('conversions_filter_pixelId')
  localStorage.removeItem('conversions_filter_ownerId')
  localStorage.removeItem('conversions_filter_datePeriod')
  localStorage.removeItem('conversions_filter_dateFrom')
  localStorage.removeItem('conversions_filter_dateTo')

  filteredPixelOptions.value = [...allPixelOptions.value]
  pagination.value.page = 1
  fetchConversions()
}

const onConversionAdded = (payload) => {
  $q.notify({
    type: 'positive',
    message: t('facebook.conversions.added_successfully'),
    position: 'top'
  })
  showAddConversionDialog.value = false

  // Обновляем список конверсий
  fetchConversions()
}

const showDetails = (conversion) => {
  currentConversion.value = conversion
  showDetailsDialog.value = true
}

// Utility methods
const formatUrl = (url) => {
  if (!url) return ''
  try {
    const urlObj = new URL(url)
    return urlObj.hostname + urlObj.pathname
  } catch {
    return url.length > 30 ? url.substring(0, 30) + '...' : url
  }
}

const getEventSourceUrl = (conversion) => {
  // Сначала проверяем source_url на уровне конверсии
  if (conversion?.source_url) {
    return conversion.source_url
  }

  // Затем проверяем в custom_data
  if (conversion?.custom_data) {
    try {
      const customData = typeof conversion.custom_data === 'string'
          ? JSON.parse(conversion.custom_data)
          : conversion.custom_data
      return customData?.event_source_url || null
    } catch {
      return null
    }
  }

  return null
}

const getReferrerUrl = (conversion) => {
  return conversion?.custom_data?.referrer_url || null
}

const getExternalId = (conversion) => {
  // Проверяем в user_data
  if (conversion?.user_data) {
    try {
      const userData = typeof conversion.user_data === 'string'
          ? JSON.parse(conversion.user_data)
          : conversion.user_data
      return userData?.external_id || null
    } catch {
      return null
    }
  }
  return null
}

const getFBC = (conversion) => {
  // Проверяем в user_data
  if (conversion?.user_data) {
    try {
      const userData = typeof conversion.user_data === 'string'
          ? JSON.parse(conversion.user_data)
          : conversion.user_data
      return userData?.fbc || null
    } catch {
      return null
    }
  }
  return null
}

const getValidationWarnings = (conversion) => {
  if (!conversion?.validation_warnings) return null

  const warnings = conversion.validation_warnings

  // Если уже объект, возвращаем как есть
  if (typeof warnings === 'object') return warnings

  // Если строка, парсим
  try {
    return JSON.parse(warnings)
  } catch {
    return null
  }
}

const formatCustomData = (data) => {
  if (!data) return t('facebook.conversions.details.no_data')
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2)
    } catch {
      return data
    }
  }
  return JSON.stringify(data, null, 2)
}

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    $q.notify({
      type: 'positive',
      message: t('facebook.conversions.actions.copied_to_clipboard'),
      timeout: 1000
    })
  })
}

const getStatusColor = (status) => {
  const colors = {
    success: 'positive',
    pending: 'warning',
    failed: 'negative'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status) => {
  const icons = {
    success: 'check_circle',
    pending: 'schedule',
    failed: 'error'
  }
  return icons[status] || 'help'
}

const getStatusText = (status) => {
  const texts = {
    success: t('facebook.conversions.status.successful'),
    pending: t('facebook.conversions.status.processing'),
    failed: t('facebook.conversions.status.failed')
  }
  return texts[status] || status
}

const getQualityColor = (score) => {
  if (score >= 8) return 'positive'
  if (score >= 6) return 'warning'
  if (score >= 4) return 'orange'
  return 'negative'
}

const getQualityText = (score) => {
  if (score >= 8) return t('facebook.conversions.quality.excellent')
  if (score >= 6) return t('facebook.conversions.quality.good')
  if (score >= 4) return t('facebook.conversions.quality.average')
  return t('facebook.conversions.quality.poor')
}

// Lifecycle
onMounted(async () => {
  // Если есть сохраненный период, но это не custom - пересчитываем даты
  const savedPeriod = localStorage.getItem('conversions_filter_datePeriod')
  if (savedPeriod && savedPeriod !== 'custom') {
    onDatePeriodChange()
  }

  await fetchConversions()
})
</script>

<style scoped>
.url-cell {
  max-width: 200px;
}

.url-link {
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: flex-start;
}

.trace-id-cell {
  max-width: 100px;
}

.custom-data-json {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.select-popup-compact .q-item {
  min-height: 40px;
}

.select-label-compact {
  font-size: 14px;
  line-height: 1.2;
}

.select-caption-compact {
  font-size: 11px;
  opacity: 0.7;
}
</style>